import { action, makeObservable, observable } from 'mobx'
import { classList } from 'react-classlist-helper'

import CalendarEvent, {
  CalendarEventEntityType,
} from '../../models/CalendarEvent'
import CompaniesStore from '../domain/Companies.store'
import ProjectDateStore, { isAfter, isBefore } from './ProjectDate.store'

export const MINUTES_IN_HOUR = 60
export const DEFAULT_TIME_INTERVAL = 60
export const HOURS_IN_DAY = 24

export const ONE_MINUTE_HEIGHT_PX = 1
const PADDING_RIGHT_SPACE = 12
export const PREVIEW_START_HOUR = 6
export const HOUR_HEIGHT_PX = ONE_MINUTE_HEIGHT_PX * MINUTES_IN_HOUR

enum CalendarViewMode {
  DEFAULT,
  NEW_EVENT,
}

export default abstract class CalendarViewStore {
  public readonly dayHours = Array.from(Array(25).keys())
  @observable public editableEventStartDate: Date = null
  @observable public editableEventEndDate: Date = null
  @observable public editableEventDataId: string = null
  @observable public mode: CalendarViewMode = CalendarViewMode.DEFAULT
  public positionedEvents: { [eventId: string]: number } = {}

  public constructor(
    protected readonly projectDateStore: ProjectDateStore,
    protected readonly companiesStore: CompaniesStore,
    protected readonly isPreviewMode?: boolean,
  ) {
    makeObservable(this)
  }

  // TODO: Investigate it deeply
  // "positionedEvents" and its restoring before render method was added by Valerii in scope of PR #2508
  // I had to add this to reset it also on "CalendarDayView.tsx"
  // Without this reset, the "getEventSizeAndPosition" method returns incorrect event positions when the component is re-render
  private columnLayoutCache = new Map<
    string,
    Map<string, { lane: number; maxLanes: number; span: number }>
  >()

  public restorePositionedEvents() {
    this.positionedEvents = {}
    this.columnLayoutCache.clear()
  }

  @action.bound
  public setNewEventMode() {
    this.mode = CalendarViewMode.NEW_EVENT
  }

  @action.bound
  public setDefaultMode() {
    this.editableEventDataId = null
    this.mode = CalendarViewMode.DEFAULT
  }

  public get timeInterval() {
    return DEFAULT_TIME_INTERVAL
  }

  protected get minStartTimeMs() {
    const { getTimeMs, startOfDay } = this.projectDateStore
    return getTimeMs(startOfDay(new Date()))
  }

  protected get maxStartTimeMs() {
    const { getTimeMs, endOfDay, addMinutes } = this.projectDateStore
    return getTimeMs(addMinutes(endOfDay(new Date()), -this.timeInterval))
  }

  protected get maxEndTimeMs() {
    const { getTimeMs, endOfDay } = this.projectDateStore
    return getTimeMs(endOfDay(new Date()))
  }

  public get rowsNumber() {
    return Math.round(MINUTES_IN_HOUR / this.timeInterval)
  }

  public getEndDate(startDate: Date) {
    const { addMinutes, isSameDay } = this.projectDateStore
    let endDate = addMinutes(startDate, this.timeInterval)
    if (!isSameDay(startDate, endDate)) {
      endDate = addMinutes(endDate, -1)
    }

    return endDate
  }

  public getNewEvent(entityType: CalendarEventEntityType): CalendarEvent {
    const startDate = this.editableEventStartDate
    const endDate = this.editableEventEndDate || this.getEndDate(startDate)

    return CalendarEvent.createNewEvent(
      startDate,
      endDate,
      this.projectDateStore,
      entityType,
    )
  }

  private computeColumnLayout(
    allEvents: CalendarEvent[],
  ): Map<string, { lane: number; maxLanes: number; span: number }> {
    const columnKey = allEvents
      .map(e => e.dataId)
      .sort()
      .join(',')

    if (this.columnLayoutCache.has(columnKey)) {
      return this.columnLayoutCache.get(columnKey)
    }

    const layout = new Map<
      string,
      { lane: number; maxLanes: number; span: number }
    >()

    const clusters: CalendarEvent[][] = []
    const visited = new Set<string>()

    allEvents.forEach(event => {
      if (visited.has(event.dataId)) return

      const cluster: CalendarEvent[] = []
      const queue = [event]

      while (queue.length > 0) {
        const current = queue.shift()
        if (visited.has(current.dataId)) continue

        visited.add(current.dataId)
        cluster.push(current)

        allEvents.forEach(other => {
          if (!visited.has(other.dataId)) {
            const currentStart = current.startDate.getTime()
            const currentEnd = current.endDate.getTime()
            const otherStart = other.startDate.getTime()
            const otherEnd = other.endDate.getTime()

            if (currentStart < otherEnd && currentEnd > otherStart) {
              queue.push(other)
            }
          }
        })
      }

      clusters.push(cluster)
    })

    clusters.forEach(cluster => {
      const eventPoints: Array<{
        time: number
        event: CalendarEvent
        isStart: boolean
      }> = []

      cluster.forEach(event => {
        const startTime = event.startDate.getTime()
        const endTime = event.endDate.getTime()

        eventPoints.push({ time: startTime, event, isStart: true })
        eventPoints.push({ time: endTime, event, isStart: false })
      })

      eventPoints.sort((a, b) => {
        if (a.time !== b.time) return a.time - b.time
        if (a.isStart !== b.isStart) return a.isStart ? 1 : -1
        const durA = a.isStart
          ? a.event.endDate.getTime() - a.event.startDate.getTime()
          : 0
        const durB = a.isStart
          ? b.event.endDate.getTime() - b.event.startDate.getTime()
          : 0
        return durB - durA
      })

      let currentConcurrent = 0
      let maxConcurrentInCluster = 0
      eventPoints.forEach(point => {
        if (point.isStart) {
          currentConcurrent++
          maxConcurrentInCluster = Math.max(
            maxConcurrentInCluster,
            currentConcurrent,
          )
        } else {
          currentConcurrent--
        }
      })

      const activeLanes = new Set<number>()
      const eventToLane = new Map<string, number>()
      const laneOccupancy = new Map<number, Array<[number, number]>>()

      for (let i = 0; i < maxConcurrentInCluster; i++) {
        laneOccupancy.set(i, [])
      }

      eventPoints.forEach(point => {
        if (point.isStart) {
          let lane = 0

          const existingLane = this.positionedEvents[point.event.dataId]
          if (
            Number.isInteger(existingLane) &&
            !activeLanes.has(existingLane)
          ) {
            lane = existingLane
          } else {
            while (activeLanes.has(lane)) {
              lane++
            }
          }

          activeLanes.add(lane)
          eventToLane.set(point.event.dataId, lane)

          const startTime = point.event.startDate.getTime()
          const endTime = point.event.endDate.getTime()
          laneOccupancy.get(lane)?.push([startTime, endTime])
        } else {
          const lane = eventToLane.get(point.event.dataId)
          activeLanes.delete(lane)
        }
      })

      cluster.forEach(event => {
        const lane = eventToLane.get(event.dataId)
        const startTime = event.startDate.getTime()
        const endTime = event.endDate.getTime()
        let span = 1

        for (
          let nextLane = lane + 1;
          nextLane < maxConcurrentInCluster;
          nextLane++
        ) {
          if (
            this.isLaneFreeForInterval(
              nextLane,
              startTime,
              endTime,
              laneOccupancy,
            )
          ) {
            span++
          } else {
            break
          }
        }

        layout.set(event.dataId, {
          lane,
          maxLanes: maxConcurrentInCluster,
          span,
        })
      })
    })

    this.columnLayoutCache.set(columnKey, layout)
    return layout
  }

  private isLaneFreeForInterval(
    lane: number,
    startTime: number,
    endTime: number,
    laneOccupancy: Map<number, Array<[number, number]>>,
  ): boolean {
    const occupancies = laneOccupancy.get(lane) || []

    return !occupancies.some(
      ([occupyStart, occupyEnd]) =>
        startTime < occupyEnd && endTime > occupyStart,
    )
  }

  public getEventSizeAndPosition = (
    event: CalendarEvent,
    allEvents: CalendarEvent[],
    currentDay: Date,
  ) => {
    let { startDate, endDate } = event
    const { isSameDay, getTimeMs, setTimeMs } = this.projectDateStore
    if (getTimeMs(startDate) < this.minStartTimeMs) {
      startDate = setTimeMs(startDate, this.minStartTimeMs)
    }

    if (getTimeMs(endDate) > this.maxEndTimeMs) {
      endDate = setTimeMs(startDate, this.maxEndTimeMs)
    }

    let startOffset = this.getOffsetByDate(startDate, currentDay)
    let endOffset = this.getOffsetByDate(endDate, currentDay)

    let blockHeight
    if (endOffset - startOffset < 0) {
      blockHeight = `calc(100% - ${startOffset}px)`
      if (isSameDay(endDate, currentDay)) {
        endOffset = startOffset
        startOffset = 0
      }
    } else {
      blockHeight = endOffset - startOffset
    }

    const columnLayout = this.computeColumnLayout(allEvents)
    const eventLayout = columnLayout.get(event.dataId)

    const lane = eventLayout.lane
    const maxLanes = eventLayout.maxLanes
    const span = eventLayout.span

    this.positionedEvents[event.dataId] = lane

    const singleLaneWidth = 100 / maxLanes
    const percentageSizePart = singleLaneWidth * span
    const widthCorrection = PADDING_RIGHT_SPACE / maxLanes
    const blockWidth = `calc(${percentageSizePart}% - ${
      widthCorrection * span
    }px)`

    const percentagePositionPart = singleLaneWidth * lane
    const pxPosition = widthCorrection * lane
    const leftOffset = `calc(${percentagePositionPart}% - ${pxPosition}px)`

    const style = Object.assign(
      {
        height: blockHeight,
        top: startOffset,
        left: leftOffset,
        width: blockWidth,
      },
      event.styles,
    )

    return {
      style,
      className: classList({
        'dashed-bottom': !isSameDay(endDate, currentDay),
        'dashed-top': !isSameDay(startDate, currentDay),
      }),
    }
  }

  public get isNewEventMode() {
    return this.mode === CalendarViewMode.NEW_EVENT
  }

  public getOffsetByDate(date: Date, currentDay: Date) {
    let [hours, minutes] = this.projectDateStore.getHoursMinutes(date)
    if (!this.projectDateStore.isSameDay(date, currentDay)) {
      if (isBefore(date, currentDay)) {
        hours = 0
        minutes = 0
      } else if (isAfter(date, currentDay)) {
        hours = 23
        minutes = 59
      }
    }

    if (this.isPreviewMode) {
      hours = hours - PREVIEW_START_HOUR
    }

    return (hours * MINUTES_IN_HOUR + minutes) * ONE_MINUTE_HEIGHT_PX
  }

  protected getSameBlockEvents(
    iteratedEvents: CalendarEvent[],
    allEvents: CalendarEvent[],
  ) {
    let oldEvents: CalendarEvent[] = []
    const newEvents: CalendarEvent[] = []

    allEvents
      .filter(e => iteratedEvents.includes(e))
      .forEach(e => {
        const definedIndex = this.positionedEvents[e.dataId]
        if (Number.isInteger(definedIndex)) {
          oldEvents[definedIndex] = e
        } else {
          newEvents.push(e)
        }
      })

    oldEvents = Object.values(oldEvents)

    newEvents.sort((a, b) => {
      const dataA = a.data
      const dataB = b.data

      if (a.startDate.getTime() !== b.startDate.getTime()) {
        return a.startDate.getTime() - b.startDate.getTime()
      }

      const durationA = a.endDate.getTime() - a.startDate.getTime()
      const durationB = b.endDate.getTime() - b.startDate.getTime()

      if (durationA !== durationB) {
        return durationB - durationA
      }

      const nameA = dataA?.codeToDisplay(this.companiesStore)
      const nameB = dataB?.codeToDisplay(this.companiesStore)

      if (nameA && !nameB) {
        return 1
      } else if (!nameA && nameB) {
        return -1
      } else if (!nameA && !nameB) {
        return 1
      } else {
        return nameA.localeCompare(nameB)
      }
    })

    let oldIndex = 0
    let newIndex = 0
    const resultArray: CalendarEvent[] = []
    for (let index = 0; index < newEvents.length + oldEvents.length; index++) {
      if (
        !!oldEvents[oldIndex] &&
        this.positionedEvents[oldEvents[oldIndex].dataId] === index
      ) {
        resultArray.push(oldEvents[oldIndex++])
      } else {
        resultArray.push(newEvents[newIndex++])
      }
    }

    return resultArray
  }

  protected getSameLineEvents(
    event: CalendarEvent,
    allEvents: CalendarEvent[],
    eventsToExclude: CalendarEvent[] = [],
  ) {
    return allEvents.filter(e => {
      return (
        !eventsToExclude.includes(e) &&
        (e.isEqual(event) || e.isIntersect(event))
      )
    })
  }

  protected validateEditableEventStartDate(baseDate: Date) {
    const editableDate = this.editableEventStartDate
    const { getTimeMs, setTimeMs, isSameDay } = this.projectDateStore
    const startTimeMs = getTimeMs(editableDate)

    const isEditableDateBeforeBaseDate =
      !isSameDay(baseDate, editableDate) && isBefore(editableDate, baseDate)
    if (startTimeMs < this.minStartTimeMs || isEditableDateBeforeBaseDate) {
      this.editableEventStartDate = setTimeMs(baseDate, this.minStartTimeMs)
    }

    const isEditableDateAfterBaseDate =
      !isSameDay(baseDate, editableDate) && isAfter(editableDate, baseDate)
    if (startTimeMs > this.maxStartTimeMs || isEditableDateAfterBaseDate) {
      this.editableEventStartDate = setTimeMs(baseDate, this.maxStartTimeMs)
    }
  }
}
